/**
 * Route to Tab Mapping
 * Maps route paths to their required tab permissions
 */

export interface RouteTabMapping {
  [key: string]: string | string[] | null;
}

/**
 * Maps routes to the tab name required to access them.
 * If a route requires no specific tab permission, set it to null.
 * Tab names should match the main_tab values from the API (case-insensitive).
 * For routes that can be accessed by multiple tabs, use an array.
 */
export const routeTabMapping: RouteTabMapping = {
  // Home page - accessible by HOME, HOTELS, or PROGRAM SUMMARY tab (shows different content based on role)
  // ALOD-268: ENTERPRISE_USER holds only the GLOBAL DIRECTORY tab. `/` is the
  // Dashboard dispatcher, which redirects that role on to the global
  // directory, but ProtectedRoute checks this list first - so without
  // GLOBAL DIRECTORY here the role is sent to /403 and the redirect in
  // Dashboard.tsx never runs.
  '/': [
    'HOME',
    'HOTELS',
    'PROGRAM SUMMARY',
    'BRAND DASHBOARD',
    'GLOBAL DIRECTORY',
  ],

  //users page - accessible by USERS tab
  '/users': 'USERS',

  // Hotels page - accessible by HOTELS tab (super admin only sees this in sidebar)
  '/hotels': 'HOTELS',
  '/hotels/create': 'HOTELS',
  '/hotel-organizations': 'HOTEL ORGANIZATIONS',
  // Detailed hotel orgs page - belongs to HOTEL ORGANIZATIONS tab only
  '/hotel-organizations/:id': 'HOTEL ORGANIZATIONS',

  // Program Summary page - accessible by PROGRAM SUMMARY tab
  '/program-summary': 'PROGRAM SUMMARY',

  // Program Properties page - accessible by PROGRAM PROPERTIES tab
  '/program-properties': 'PROGRAM PROPERTIES',

  // Enterprises page - accessible by ENTERPRISES tab
  '/enterprises': 'ENTERPRISES',
  '/parent-enterprises': 'PARENT ENTERPRISES',
  '/maintenance': 'MAINTENANCE',
  '/hotel-brands': 'HOTEL BRANDS',
  // Boost Your Business
  '/upgrade': 'BOOST YOUR BUSINESS',

  // Import section
  '/import': 'IMPORT',

  // Reporting - different routes for different roles
  '/reporting': 'REPORTING',

  // Hotel details - accessible by HOTELS tab (super admin, hotel roles) or BRAND HOTELS tab (brand admins)
  '/hotels/:id': ['HOTELS', 'BRAND HOTELS'],

  // Help - external link, no restriction needed
  '/help': null,

  // Our Approach
  '/our-approach': 'OUR APPROACH',

  // About
  '/about': 'ABOUT ALŌ INDEX',

  // Sample Questionnaire
  '/sample-questionnaire': 'SAMPLE QUESTIONNAIRE',

  // Terms of Use
  '/terms-of-use': 'TERMS OF USE',
  // Travel Manager routes - ENTERPRISE_ADMIN role only
  '/travel-managers/program-properties': 'PROGRAM PROPERTIES',
  '/travel-managers/global-directory': 'GLOBAL DIRECTORY',
  '/travel-managers/program-summary': 'PROGRAM SUMMARY',
  '/travel-managers/cpv-insights': 'CORPORATE PRIORITY VIEW',
  '/travel-managers/non-hotel-venues': 'NON-HOTEL VENUES',
  '/travel-managers/program-builder': 'PROGRAM BUILDER',
  '/travel-managers/brand-performance': 'BRAND PERFORMANCE',
  '/travel-managers/reporting': 'REPORTING',

  '/integrations/instructions': 'INTEGRATIONS',

  '/brand-admin/enterprise-hotels/:enterpriseId': 'BRAND DASHBOARD',

  // Profile - everyone can access
  '/profile': null,

  // Privacy Policy - everyone can access
  '/privacy-policy': null,

  // Error pages - everyone can access
  '/500': null,
  '/403': null,
  '*': null,
};

/**
 * Get the required tab for a given route path
 * @param path - The route path to check
 * @returns The required tab name(s) or null if no restriction
 */
export const getRequiredTabForRoute = (
  path: string
): string | string[] | null => {
  // Direct match
  if (routeTabMapping[path] !== undefined) {
    return routeTabMapping[path];
  }

  // Check for dynamic routes (e.g., /hotels/:id)
  for (const [routePattern, tabName] of Object.entries(routeTabMapping)) {
    if (routePattern.includes(':')) {
      // Convert route pattern to regex
      const regexPattern = routePattern.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${regexPattern}$`);

      if (regex.test(path)) {
        return tabName;
      }
    }
  }

  // Default - no restriction
  return null;
};

/**
 * Check if a user has access to a specific route
 * @param path - The route path to check
 * @param userTabs - Array of tab names the user has access to (uppercase)
 * @returns true if user has access, false otherwise
 */
export const hasAccessToRoute = (path: string, userTabs: string[]): boolean => {
  const requiredTab = getRequiredTabForRoute(path);

  // If no tab is required, allow access
  if (requiredTab === null) {
    return true;
  }

  // If requiredTab is an array, check if user has any of the required tabs
  if (Array.isArray(requiredTab)) {
    return requiredTab.some(tab =>
      userTabs.some(userTab => userTab.toUpperCase() === tab.toUpperCase())
    );
  }

  // If requiredTab is a string, check if user has that tab (case-insensitive)
  if (typeof requiredTab === 'string') {
    return userTabs.some(
      tab => tab.toUpperCase() === requiredTab.toUpperCase()
    );
  }

  return false;
};
